import React, { useState } from 'react'
import { exists, onlyPopulatedValues } from '@unimpaired/utils'
import * as R from 'ramda'
import { UseFormReturn } from 'react-hook-form'
import { Button } from '../../../Button'
import { navigateToSlideIndex } from '../../../Animations/Slider/slider.helpers'
import { StepProps } from '../../Step'

type Props = {
  stepProps: StepProps[]
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>
  currentStepIndex: number
  /** Optional — only present when the Stepper was given a `form`. */
  form?: UseFormReturn<any>
  /** Whether the current step's gated `fields` currently have rhf errors. */
  fieldsHaveErrors: boolean
}

export const FooterBar = ({ stepProps, ...props }: Props) => {
  const onLastStep = props.currentStepIndex === stepProps.length - 1
  const onFirstStep = props.currentStepIndex === 0
  const selectedStep = stepProps[props.currentStepIndex]
  const [advancing, setAdvancing] = useState(false)

  const stepErrors = onlyPopulatedValues(selectedStep?.errors || [])
  const isDisabled =
    !!selectedStep?.disabled || props.fieldsHaveErrors || advancing

  const handleNext = async () => {
    // Auto-trigger field validation to mirror previous Stepper behavior.
    if (props.form && selectedStep?.fields?.length) {
      const baseFields = R.uniq(
        selectedStep.fields.map((f) => (f.includes('.') ? f.split('.')[0] : f)),
      )
      const ok = await props.form.trigger(baseFields)
      if (!ok) return
    }

    setAdvancing(true)
    try {
      // onNext: sync/absent advances immediately; async is awaited; returning false cancels.
      const result = selectedStep?.onNext?.()
      const settled = result instanceof Promise ? await result : result
      if (settled === false) return
      if (onLastStep) return
      props.setCurrentStepIndex(
        navigateToSlideIndex(
          props.currentStepIndex + 1,
          props.currentStepIndex,
          stepProps,
        ),
      )
    } finally {
      setAdvancing(false)
    }
  }

  return (
    <footer className='relative grow-0 transition-all flex-none flex justify-between items-center w-full px-4 py-2 bg-white border-t'>
      <div className='flex mx-auto items-center justify-between max-w-5xl w-full'>
        {onFirstStep ? (
          <div />
        ) : (
          <Button
            variant='raised'
            type='button'
            onClick={() => {
              selectedStep?.onPrevious?.()
              props.setCurrentStepIndex(
                navigateToSlideIndex(
                  props.currentStepIndex - 1,
                  props.currentStepIndex,
                  stepProps,
                ),
              )
            }}>
            Previous
          </Button>
        )}

        {exists(stepErrors) && (
          <p className='text-red-400 hidden sm:block font-semibold max-w-5xl pt-2'>
            [ERROR]: {stepErrors.join(', ')}
          </p>
        )}

        <div className='flex items-center gap-2'>
          {selectedStep?.footerContent}

          {!selectedStep?.hideNext && (
            <Button
              variant='raised'
              type={selectedStep?.formSubmit ? 'submit' : 'button'}
              loading={advancing}
              disabled={isDisabled}
              onClick={handleNext}>
              {onLastStep ? 'Complete' : 'Next'}
            </Button>
          )}
        </div>
      </div>
    </footer>
  )
}
